<template>
  <v-dialog
    :value="value"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    @input="$emit('input', $event)"
    max-width="720"
    persistent
    scrollable
    content-class="job-application-dialog">
    <v-card dark class="dialog-card">
      <v-card-title class="dialog-header">
        <div class="dialog-header__copy">
          <span class="dialog-header__icon" aria-hidden="true">
            <v-icon>mdi-pencil-outline</v-icon>
          </span>
          <div>
            <div class="dialog-eyebrow">Bewerberprofil</div>
            <h2>Bewerbung bearbeiten</h2>
            <p>Kontaktangaben, Pensum und Adresse aktualisieren.</p>
          </div>
        </div>
        <v-btn icon class="dialog-close" @click="closeDialog" :disabled="isSaving" title="Dialog schliessen" aria-label="Dialog schliessen">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="dialog-divider"></v-divider>

      <v-card-text class="dialog-content">
        <v-form ref="editForm" class="form-stack" @submit.prevent="saveApplication">
          <span class="sr-only" role="status" aria-live="polite">{{ copyFeedback }}</span>

          <section class="form-section">
            <div class="section-heading">
              <span class="section-heading__icon"><v-icon>mdi-account-outline</v-icon></span>
              <div>
                <h3>Persönliche Angaben</h3>
                <p>Grundlegende Kontaktinformationen der bewerbenden Person.</p>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field v-model="formData.firstName" label="Vorname" outlined dense required>
                  <template v-slot:append>
                    <v-btn
                      icon
                      type="button"
                      class="copy-field-action"
                      :class="{ 'copy-field-action--copied': copiedField === 'firstName' }"
                      :disabled="!hasCopyValue(formData.firstName)"
                      :title="copiedField === 'firstName' ? 'Vorname kopiert' : 'Vorname kopieren'"
                      :aria-label="copiedField === 'firstName' ? 'Vorname kopiert' : 'Vorname kopieren'"
                      @click.stop="copyToClipboard(formData.firstName, 'Vorname', 'firstName')">
                      <v-icon small>{{ copiedField === "firstName" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="formData.lastName" label="Nachname" outlined dense required>
                  <template v-slot:append>
                    <v-btn
                      icon
                      type="button"
                      class="copy-field-action"
                      :class="{ 'copy-field-action--copied': copiedField === 'lastName' }"
                      :disabled="!hasCopyValue(formData.lastName)"
                      :title="copiedField === 'lastName' ? 'Nachname kopiert' : 'Nachname kopieren'"
                      :aria-label="copiedField === 'lastName' ? 'Nachname kopiert' : 'Nachname kopieren'"
                      @click.stop="copyToClipboard(formData.lastName, 'Nachname', 'lastName')">
                      <v-icon small>{{ copiedField === "lastName" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="formData.email" label="E-Mail" type="email" outlined dense>
                  <template v-slot:append>
                    <v-btn
                      icon
                      type="button"
                      class="copy-field-action"
                      :class="{ 'copy-field-action--copied': copiedField === 'email' }"
                      :disabled="!hasCopyValue(formData.email)"
                      :title="copiedField === 'email' ? 'E-Mail kopiert' : 'E-Mail kopieren'"
                      :aria-label="copiedField === 'email' ? 'E-Mail kopiert' : 'E-Mail kopieren'"
                      @click.stop="copyToClipboard(formData.email, 'E-Mail', 'email')">
                      <v-icon small>{{ copiedField === "email" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="formData.phone" label="Telefon" outlined dense>
                  <template v-slot:append>
                    <v-btn
                      icon
                      type="button"
                      class="copy-field-action"
                      :class="{ 'copy-field-action--copied': copiedField === 'phone' }"
                      :disabled="!hasCopyValue(formData.phone)"
                      :title="copiedField === 'phone' ? 'Telefonnummer kopiert' : 'Telefonnummer kopieren'"
                      :aria-label="copiedField === 'phone' ? 'Telefonnummer kopiert' : 'Telefonnummer kopieren'"
                      @click.stop="copyToClipboard(formData.phone, 'Telefonnummer', 'phone')">
                      <v-icon small>{{ copiedField === "phone" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="formData.desiredWorkload" label="Gewünschtes Pensum (z.B. 80-100%)" outlined dense>
                  <template v-slot:append>
                    <v-btn
                      icon
                      type="button"
                      class="copy-field-action"
                      :class="{ 'copy-field-action--copied': copiedField === 'desiredWorkload' }"
                      :disabled="!hasCopyValue(formData.desiredWorkload)"
                      :title="copiedField === 'desiredWorkload' ? 'Pensum kopiert' : 'Pensum kopieren'"
                      :aria-label="copiedField === 'desiredWorkload' ? 'Pensum kopiert' : 'Pensum kopieren'"
                      @click.stop="copyToClipboard(formData.desiredWorkload, 'Pensum', 'desiredWorkload')">
                      <v-icon small>{{ copiedField === "desiredWorkload" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <div class="section-heading">
              <span class="section-heading__icon"><v-icon>mdi-map-marker-outline</v-icon></span>
              <div>
                <h3>Adresse</h3>
                <p>Aktuelle Wohn- und Korrespondenzadresse.</p>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12">
                <v-text-field v-model="formData.address.street" label="Strasse & Hausnummer" outlined dense>
                  <template v-slot:append>
                    <v-btn
                      icon
                      type="button"
                      class="copy-field-action"
                      :class="{ 'copy-field-action--copied': copiedField === 'street' }"
                      :disabled="!hasCopyValue(formData.address.street)"
                      :title="copiedField === 'street' ? 'Strasse kopiert' : 'Strasse kopieren'"
                      :aria-label="copiedField === 'street' ? 'Strasse kopiert' : 'Strasse kopieren'"
                      @click.stop="copyToClipboard(formData.address.street, 'Strasse', 'street')">
                      <v-icon small>{{ copiedField === "street" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="formData.address.zip" label="PLZ" outlined dense>
                  <template v-slot:append>
                    <v-btn
                      icon
                      type="button"
                      class="copy-field-action"
                      :class="{ 'copy-field-action--copied': copiedField === 'zip' }"
                      :disabled="!hasCopyValue(formData.address.zip)"
                      :title="copiedField === 'zip' ? 'PLZ kopiert' : 'PLZ kopieren'"
                      :aria-label="copiedField === 'zip' ? 'PLZ kopiert' : 'PLZ kopieren'"
                      @click.stop="copyToClipboard(formData.address.zip, 'PLZ', 'zip')">
                      <v-icon small>{{ copiedField === "zip" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field v-model="formData.address.city" label="Ort" outlined dense>
                  <template v-slot:append>
                    <v-btn
                      icon
                      type="button"
                      class="copy-field-action"
                      :class="{ 'copy-field-action--copied': copiedField === 'city' }"
                      :disabled="!hasCopyValue(formData.address.city)"
                      :title="copiedField === 'city' ? 'Ort kopiert' : 'Ort kopieren'"
                      :aria-label="copiedField === 'city' ? 'Ort kopiert' : 'Ort kopieren'"
                      @click.stop="copyToClipboard(formData.address.city, 'Ort', 'city')">
                      <v-icon small>{{ copiedField === "city" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </section>

          <section v-if="hasCopyValue(application.coverLetter)" class="form-section application-text-section">
            <div class="section-heading section-heading--with-action">
              <span class="section-heading__icon"><v-icon>mdi-text-box-outline</v-icon></span>
              <div>
                <h3>Bewerbungstext</h3>
                <p>Der vollständige Text kann markiert oder direkt kopiert werden.</p>
              </div>
              <v-btn
                outlined
                type="button"
                class="copy-text-action"
                :class="{ 'copy-text-action--copied': copiedField === 'coverLetter' }"
                :title="copiedField === 'coverLetter' ? 'Bewerbungstext kopiert' : 'Bewerbungstext kopieren'"
                :aria-label="copiedField === 'coverLetter' ? 'Bewerbungstext kopiert' : 'Bewerbungstext kopieren'"
                @click="copyToClipboard(application.coverLetter, 'Bewerbungstext', 'coverLetter')">
                <v-icon left small>{{ copiedField === "coverLetter" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                {{ copiedField === "coverLetter" ? "Kopiert" : "Text kopieren" }}
              </v-btn>
            </div>
            <div class="application-text" tabindex="0">{{ application.coverLetter }}</div>
          </section>
        </v-form>
      </v-card-text>

      <v-divider class="dialog-divider"></v-divider>

      <v-card-actions class="dialog-footer">
        <v-spacer></v-spacer>
        <v-btn text class="secondary-action" @click="closeDialog" :disabled="isSaving">Abbrechen</v-btn>
        <v-btn color="primary" depressed class="primary-action" @click="saveApplication" :loading="isSaving">
          <v-icon left small>mdi-content-save-outline</v-icon>
          Speichern
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  name: "JobApplicationEditDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    application: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isSaving: false,
      copiedField: null,
      copyFeedback: "",
      copyResetTimer: null,
      formData: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        desiredWorkload: "",
        address: {
          street: "",
          zip: "",
          city: "",
        },
      },
    }
  },
  watch: {
    value(isOpen) {
      if (isOpen && this.application) {
        this.initializeForm()
      }
    },
  },
  methods: {
    hasCopyValue(value) {
      return value !== null && value !== undefined && String(value).trim().length > 0
    },
    async copyToClipboard(value, label, fieldKey) {
      if (!this.hasCopyValue(value)) return

      const text = String(value).trim()
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          try {
            await navigator.clipboard.writeText(text)
          } catch (clipboardError) {
            this.copyWithFallback(text)
          }
        } else {
          this.copyWithFallback(text)
        }

        this.copiedField = fieldKey
        this.copyFeedback = `${label} kopiert`
        if (this.$toast) this.$toast.success(`${label} kopiert`)

        if (this.copyResetTimer) clearTimeout(this.copyResetTimer)
        this.copyResetTimer = setTimeout(() => {
          this.copiedField = null
          this.copyFeedback = ""
          this.copyResetTimer = null
        }, 1800)
      } catch (error) {
        this.copiedField = null
        this.copyFeedback = `${label} konnte nicht kopiert werden`
        if (this.$toast) this.$toast.error(`${label} konnte nicht kopiert werden`)
      }
    },
    copyWithFallback(text) {
      const textarea = document.createElement("textarea")
      textarea.value = text
      textarea.setAttribute("readonly", "")
      textarea.style.position = "fixed"
      textarea.style.top = "-9999px"
      textarea.style.opacity = "0"
      document.body.appendChild(textarea)
      textarea.select()

      let copied = false
      try {
        copied = document.execCommand("copy")
      } finally {
        document.body.removeChild(textarea)
      }
      if (!copied) throw new Error("Clipboard API nicht verfügbar")
    },
    initializeForm() {
      this.formData = {
        firstName: this.application.firstName || "",
        lastName: this.application.lastName || "",
        email: this.application.email || "",
        phone: this.application.phone || "",
        desiredWorkload: this.application.desiredWorkload || "",
        address: {
          street: this.application.address?.street || "",
          zip: this.application.address?.zip || "",
          city: this.application.address?.city || "",
        },
      }
    },
    closeDialog() {
      this.$emit("input", false)
    },
    async saveApplication() {
      this.isSaving = true
      try {
        const updatedApplication = await api.jobApplications.update(this.application._id, this.formData)
        if (this.$toast) this.$toast.success("Bewerbung erfolgreich aktualisiert")
        this.$emit("saved", updatedApplication)
      } catch (error) {
        console.error("Fehler beim Speichern:", error)
        if (this.$toast) this.$toast.error("Bewerbung konnte nicht gespeichert werden")
      } finally {
        this.isSaving = false
      }
    },
  },
  mounted() {
    if (this.value) this.initializeForm()
  },
  beforeDestroy() {
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer)
  },
}
</script>

<style scoped>
.dialog-card {
  --dialog-primary: #009640;
  --dialog-primary-hover: #00a848;
  --dialog-border: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 24px !important;
  color: #f4f4f5;
  background: radial-gradient(circle at 92% -8%, rgba(0, 150, 64, 0.09), transparent 22rem), #202021 !important;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.58) !important;
}

.dialog-header {
  display: flex;
  flex: 0 0 auto;
  min-height: 112px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 24px 26px !important;
  background: rgba(0, 0, 0, 0.2);
}

.dialog-header__copy {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 15px;
}

.dialog-header__icon,
.section-heading__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 150, 64, 0.3);
  color: #d8f3e2;
  background: rgba(0, 150, 64, 0.14);
}

.dialog-header__icon {
  width: 48px;
  height: 48px;
  border-radius: 15px;
}

.dialog-header__icon .v-icon,
.section-heading__icon .v-icon {
  color: inherit;
}

.dialog-eyebrow {
  margin-bottom: 4px;
  color: #65ca8b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.dialog-header h2 {
  margin: 0;
  color: #ffffff;
  font-size: 23px;
  font-weight: 750;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.dialog-header p {
  margin: 5px 0 0;
  color: #9d9da2;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
}

.dialog-close {
  flex: 0 0 auto;
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  border-radius: 13px;
  color: #b8b8bd !important;
}

.dialog-close:hover,
.dialog-close:focus-visible {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.07);
}

.dialog-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.dialog-content {
  max-height: min(72vh, 760px);
  padding: 22px 24px 26px !important;
}

.form-stack {
  display: grid;
  gap: 14px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.form-section {
  padding: 19px;
  border: 1px solid var(--dialog-border);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
}

.section-heading {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-bottom: 16px;
}

.section-heading--with-action {
  align-items: center;
}

.section-heading--with-action > div {
  min-width: 0;
}

.section-heading__icon {
  width: 34px;
  height: 34px;
  border-radius: 11px;
}

.section-heading__icon .v-icon {
  font-size: 18px;
}

.section-heading h3 {
  margin: 1px 0 0;
  color: #eeeeef;
  font-size: 15px;
  font-weight: 720;
  line-height: 1.3;
}

.section-heading p {
  margin: 3px 0 0;
  color: #8f8f94;
  font-size: 12px;
  line-height: 1.4;
}

.dialog-card ::v-deep .v-input__slot {
  border-radius: 13px !important;
  background: rgba(0, 0, 0, 0.14) !important;
}

.dialog-card ::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.14);
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.dialog-card ::v-deep .v-input--is-focused fieldset {
  border-width: 1px;
  border-color: var(--dialog-primary) !important;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.12);
}

.dialog-card ::v-deep .v-label {
  color: #9d9da2;
}

.copy-field-action {
  width: 36px !important;
  min-width: 36px !important;
  height: 36px !important;
  margin: -6px -7px -6px 0;
  border-radius: 10px;
  color: #a7a7ac !important;
}

.copy-field-action:not(.v-btn--disabled):hover,
.copy-field-action:not(.v-btn--disabled):focus-visible {
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.14);
}

.copy-field-action--copied {
  color: #65ca8b !important;
  background: rgba(0, 150, 64, 0.14);
}

.copy-text-action {
  flex: 0 0 auto;
  min-height: 40px;
  margin-left: auto;
  border-color: rgba(0, 150, 64, 0.34) !important;
  border-radius: 12px;
  color: #d8f3e2 !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.copy-text-action:hover,
.copy-text-action:focus-visible,
.copy-text-action--copied {
  border-color: rgba(0, 150, 64, 0.54) !important;
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.14);
}

.application-text {
  max-height: 300px;
  padding: 16px;
  overflow: auto;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  color: #d4d4d8;
  background: rgba(0, 0, 0, 0.16);
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  cursor: text;
  user-select: text;
}

.application-text:focus-visible {
  outline: 2px solid rgba(101, 202, 139, 0.9);
  outline-offset: 2px;
}

.dialog-footer {
  flex: 0 0 auto;
  min-height: 76px;
  gap: 8px;
  padding: 14px 20px !important;
  background: rgba(18, 18, 19, 0.92);
  backdrop-filter: blur(16px);
}

.secondary-action,
.primary-action {
  min-height: 44px;
  border-radius: 13px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.secondary-action {
  color: #c3c3c7 !important;
}

.primary-action {
  padding-inline: 18px !important;
  color: #ffffff !important;
  background: var(--dialog-primary) !important;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.22);
}

.primary-action:hover,
.primary-action:focus-visible {
  background: var(--dialog-primary-hover) !important;
}

.primary-action.v-btn--disabled {
  color: rgba(255, 255, 255, 0.34) !important;
  background: rgba(255, 255, 255, 0.075) !important;
  box-shadow: none;
}

.dialog-card ::v-deep .v-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.25) !important;
}

@media (max-width: 959px) {
  .dialog-card {
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-content {
    max-height: none;
  }
}

@media (max-width: 599px) {
  .dialog-card {
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-header {
    min-height: auto;
    padding: 18px 16px !important;
  }

  .dialog-header__copy {
    align-items: flex-start;
    gap: 11px;
  }

  .dialog-header__icon {
    width: 42px;
    height: 42px;
    border-radius: 13px;
  }

  .dialog-header h2 {
    font-size: 19px;
  }

  .dialog-header p {
    display: none;
  }

  .dialog-content {
    max-height: none;
    padding: 14px !important;
  }

  .form-section {
    padding: 15px;
    border-radius: 16px;
  }

  .section-heading--with-action {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .copy-text-action {
    width: calc(100% - 45px);
    margin-left: 45px;
  }

  .application-text {
    max-height: 42vh;
    padding: 14px;
    font-size: 12px;
  }

  .dialog-footer {
    min-height: auto;
    padding: 11px 12px !important;
  }

  .dialog-footer .v-spacer {
    display: none;
  }

  .secondary-action,
  .primary-action {
    flex: 1 1 0;
  }
}
</style>
